import React, { useState, useEffect, useRef } from 'react';
import {
  format,
  differenceInMinutes,
  formatISO,
  setMinutes,
  add,
} from 'date-fns';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Box as MuiBox,
  Button as MuiButton,
  InputAdornment,
  DialogActions,
} from '@mui/material';

import { FormCheckBox, Input } from 'components/ui';
import Icon from 'components/ui/Icon';
import Typography from 'components/ui/Typography';
import useTimeSlotsRequest from '../hooks/useTimeSlotsRequest';
import { Dialog } from 'components';
import {
  SSlider,
  STooltip,
  SMark,
  STimeTag,
  CalendarWrapper,
  SlotsWrapper,
  EmptyMessage,
} from '../styled/chooseDateDialog';
import DialogCalendarComponent from 'components/DialogCalendar/DialogCalendarComponent';

import { ChooseDateDialogStyling } from '../styled/chooseDateDialogStyling';
import { Field } from 'react-final-form';
import LoadingPage from 'components/LoadingPage';

const currentDate = new Date();

const maxCalendarDate = new Date();

maxCalendarDate.setFullYear(currentDate.getFullYear() + 10);
maxCalendarDate.setMonth(11);

// function SelectDuration({ value, onChange }) {
//   const [error, setError] = useState(false);
//   const { t } = useTranslation();

//   const handleChange = (e) => {
//     const newValue = parseInt(e.target.value, 10);
//     if (newValue < 1 || newValue > 180) {
//       setError(true);
//       return;
//     }
//     setError(false);
//     onChange(newValue);
//   };

//   return (
//     <MuiBox display="flex" alignItems="center">
//       <Input
//         type="number"
//         placeholder="Duration"
//         variant="outlined"
//         value={value}
//         onChange={handleChange}
//         InputProps={{
//           endAdornment: <InputAdornment position="end">{t('Min')}</InputAdornment>,
//           startAdornment: (
//             <InputAdornment position="start">
//               {t('Appointment Duration:')}
//             </InputAdornment>
//           ),
//         }}
//         error={error}
//         helperText={
//           error && <Typography>{t('Date Error')}</Typography>
//         }
//       />
//     </MuiBox>
//   );
// }

const ChooseDateDialog = (props) => {
  const { close, initialData, userId } = props;
  const { t } = useTranslation();
  const isRescheduleMode = initialData?.isRescheduleMode;
  const [duration, setDuration] = useState(initialData?.selectedDuration || 30);
  const [selectedDate, setSelectedDate] = useState(
    initialData?.selectedDate?.startDate
      ? new Date(initialData?.selectedDate?.startDate)
      : currentDate
  );
  const [selectedMonth, setSelectedMonth] = useState(
    initialData?.selectedDate?.startDate
      ? new Date(initialData?.selectedDate?.startDate)
      : currentDate
  );
  const [isRange, setIsRange] = useState(initialData?.isRangeOfDates || false);
  const [selectedTime, setSelectedTime] = useState(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    isRange
      ? initialData?.selectedTimeSlot?.startDate &&
        setSelectedDate([
          initialData?.selectedTimeSlot?.startDate,
          initialData?.selectedTimeSlot?.endDate,
        ])
      : initialData?.selectedTimeSlot?.startDate &&
        setSelectedDate(initialData?.selectedTimeSlot?.startDate);
  }, [initialData]);

  useEffect(() => {
    if (Array.isArray(selectedDate)) {
      setSelectedTime({
        startDate: selectedDate?.[0],
        endDate: selectedDate?.[1],
      });
      setSelectedMonth(selectedDate?.[0]);
    } else {
      setSelectedTime({ startDate: null, endDate: null });
      setSelectedMonth(selectedDate);
    }
  }, [selectedDate]);

  const { timeSlots, loading, slots } = useTimeSlotsRequest(
    selectedDate,
    duration,
    userId || initialData.userId,
    initialData.reservationModeId,
    initialData.roomId
  );

  const onSave = () => {
    close({ selectedTime, planifiedDuration: duration, isRange: isRange });
    initialData?.form?.change('date', selectedTime);
  };

  const actions = (
    <MuiBox mb={3} display="flex" alignItems="center">
      <div
        style={{
          marginRight: '15px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" color="success">
          {isRange && selectedDate?.[1]
            ? `${t('From')} ${
                selectedDate?.[0] &&
                `${t(format(selectedDate?.[0], 'MMMM'))} ${format(
                  selectedDate?.[0],
                  'd'
                )}`
              } ${t('to')} ${
                selectedDate?.[1] &&
                `${t(format(selectedDate?.[1], 'MMMM'))} ${format(
                  selectedDate?.[1],
                  'd'
                )}`
              }`
            : selectedDate &&
              `${t(format(selectedDate, 'MMMM'))} ${format(
                selectedDate,
                'd, yyyy'
              )}`}
        </Typography>
      </div>

      <MuiButton
        type="submit"
        color="primary"
        variant="contained"
        onClick={onSave}
        disabled={!selectedTime?.startDate}
      >
        {t('Validate this date')}
      </MuiButton>
    </MuiBox>
  );

  return (
    <>
      <Dialog
        title={isRescheduleMode ? t('Reschedule date') : t('Choose date')}
        maxWidth="auto"
        onClose={close}
        {...props}
      >
        <ChooseDateDialogStyling>
          <MuiBox display="grid" height="80vh">
            <DialogCalendarComponent
              name="date"
              selectedDate={selectedDate}
              maxCalendarDate={maxCalendarDate}
              setSelectedDate={setSelectedDate}
              setSelectedMonth={setSelectedMonth}
              selectRange={isRange}
              selectedMonth={selectedMonth}
              currentDate={currentDate}
              calendarRef={calendarRef}
            />
            <MuiBox display="flex" mt={3}>
              <CalendarWrapper width={600} paddingRight={2}>
                <MuiBox
                  display="flex"
                  justifyContent="space-between"
                  paddingRight={3}
                >
                  <SLabel>4 - {t('Select the time slot')}</SLabel>
                  {isRange && (
                    <Typography>
                      {selectedDate?.[1] && t('Availability from')}
                      {selectedDate?.[0] &&
                        `${format(selectedDate[0], ' MMMM d')} ${t('to')}`}
                      {selectedDate?.[1] && format(selectedDate[1], ' d')}
                    </Typography>
                  )}
                </MuiBox>

                {isRange ? (
                  <EmptyMessage>
                    <SWarningIcon icon="exclamation-triangle" type="far" />
                    <span>
                      {t('No slots available on the selected date range')}
                    </span>
                  </EmptyMessage>
                ) : (
                  <SlotsWrapper>
                    {loading ? (
                      <LoadingPage style={{ width: '100%', height: 180 }} />
                    ) : timeSlots && timeSlots.length ? (
                      slots.map((item, index) => {
                        return (
                          <STimeTag
                            key={index}
                            isSelected={
                              selectedTime?.startDate &&
                              differenceInMinutes(
                                item?.startTime,
                                selectedTime?.startDate
                              ) === 0
                            }
                            onClick={() =>
                              setSelectedTime({
                                startDate: item?.startTime,
                                endDate: add(item?.startTime, {
                                  minutes: duration,
                                }),
                              })
                            }
                          >
                            {format(item?.startTime, "HH':'mm")}
                          </STimeTag>
                        );
                      })
                    ) : (
                      <EmptyMessage>
                        {t(
                          'On the selected dates, the patient will select an available time slot'
                        )}
                      </EmptyMessage>
                    )}
                  </SlotsWrapper>
                )}
                <FormCheckBox
                  label={t('Patient can choose any available slot')}
                  name="isRange"
                  value={isRange}
                  defaultValue={isRange}
                  onChange={() => {
                    setIsRange(!isRange);
                    if (isRange) {
                      setSelectedDate((curr) => curr[0]);
                    } else {
                      if (calendarRef.current) {
                        calendarRef.current.updater.enqueueSetState(
                          calendarRef.current,
                          {
                            value: [
                              selectedDate,
                              add(selectedDate, { days: 2 }),
                            ],
                          }
                        );
                      }
                      setSelectedDate((curr) => [curr, add(curr, { days: 2 })]);
                    }
                  }}
                />
              </CalendarWrapper>
              <CalendarWrapper
                display="flex"
                flexDirection="column"
                width="48%"
                paddingLeft={4}
              >
                <SLabel>
                  5 - {t('Select the duration of the appointment')}
                </SLabel>
                <MuiBox
                  paddingY={2}
                  paddingRight={2}
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Field name="planifiedDuration" defaultValue={duration}>
                    {({ input, meta }) => {
                      return (
                        <SSlider
                          defaultValue={duration}
                          step={15}
                          min={0}
                          max={120}
                          valueLabelDisplay="on"
                          marks
                          valueLabelFormat={(value) => (
                            <STooltip>{value} min</STooltip>
                          )}
                          value={duration}
                          onChange={(e, value) => {
                            !isRange &&
                              selectedTime?.startDate &&
                              setSelectedTime((curr) => ({
                                startDate: curr.startDate,
                                endDate: add(curr.startDate, {
                                  minutes: value,
                                }),
                              }));
                            setDuration(value);
                            input.onChange(value);
                          }}
                        />
                      );
                    }}
                  </Field>

                  {/* <MuiBox display="flex" justifyContent="space-between">
                  <SMark>5min</SMark>
                  <SMark>120min</SMark>
                </MuiBox> */}
                </MuiBox>
              </CalendarWrapper>
            </MuiBox>
            <DialogActions>{actions}</DialogActions>
          </MuiBox>
        </ChooseDateDialogStyling>
      </Dialog>
    </>
  );
};

const SLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.colors.gray400};
  margin-bottom: 10px;
  font-weight: 600;
`;

const SWarningIcon = styled(Icon)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 120px;
  color: ${({ theme }) => theme.palette.colors.gray200};
  z-index: 0;
`;

export default ChooseDateDialog;
